@file:Suppress("TooManyFunctions")

package top.wcpe.allincore.ui.runtime.event

import top.wcpe.allincore.layout.engine.LayoutResult
import top.wcpe.allincore.ui.runtime.element.Button
import top.wcpe.allincore.ui.runtime.element.DragReorder
import top.wcpe.allincore.ui.runtime.element.ScrollableContainer
import top.wcpe.allincore.ui.runtime.element.Slot
import top.wcpe.allincore.ui.runtime.element.UIElement
import top.wcpe.allincore.ui.runtime.element.VirtualGrid
import top.wcpe.allincore.ui.runtime.element.VirtualList
import top.wcpe.allincore.ui.runtime.internal.debug.uiVerbose
import top.wcpe.allincore.ui.runtime.internal.log.UiLog
import top.wcpe.allincore.ui.runtime.internal.time.currentTimeMillis
import top.wcpe.allincore.ui.runtime.ir.eval.OverlayEvalContext
import top.wcpe.allincore.ui.runtime.render.RippleAnimator
import top.wcpe.allincore.ui.runtime.render.cursorSink
import top.wcpe.allincore.ui.runtime.render.model.CursorType
import top.wcpe.allincore.ui.runtime.render.overlayOrchestrator
import kotlin.jvm.JvmOverloads
import kotlin.jvm.JvmStatic

/**
 * 输入事件分发器。
 *
 * 负责将平台层的鼠标 / 键盘事件路由到 UI 元素树中合适的元素。
 *
 * 对点击事件采用「最深命中 + 子优先冒泡」策略：先 DFS 找到最深的命中元素，
 * 再沿祖先链向上寻找第一个具备对应处理器的元素。
 */
@Suppress("LargeClass", "MagicNumber")
object InputDispatcher {

    private val LOGGER = UiLog("top.wcpe.allincore.ui.runtime.event.InputDispatcher")

    // 触发器名称常量
    private const val TRIGGER_PRESS = "press"
    private const val TRIGGER_IDLE = "idle"
    private const val TRIGGER_HOVER = "hover"
    private const val TRIGGER_LEAVE = "leave"

    /** KeyEvent.action：0=release，1=press，2=repeat。 */
    private const val ACTION_RELEASE = 0

    /** 鼠标右键编号。 */
    private const val BUTTON_RIGHT = 1

    private val KEY_UNHANDLED = KeyDispatchResult(handled = false, consumed = false)
    private val KEY_HANDLED_AND_CONSUMED = KeyDispatchResult(handled = true, consumed = true)

    /** 最后一次输入事件的时间戳（idle 触发器用） */
    private var lastInputMs: Long = 0L

    /** 获取最后一次输入事件的时间戳。 */
    @JvmStatic
    fun getLastInputMs(): Long = lastInputMs

    /** 记录输入时间戳（所有输入事件入口调用）。 */
    @JvmStatic
    fun markInput(nowMs: Long) {
        lastInputMs = nowMs
    }

    /** 平台层 mouseReleased 时调用，清空鼠标捕获并触发 release 动画。 */
    @JvmStatic
    fun releaseDragCapture() {
        DragCaptureManager.releaseDragCapture()
    }

    /**
     * 设置当前命中线程的 root 屏幕偏移。
     *
     * UIRuntimeScreen.mouseClicked 调 dispatchClick 前传入的 mx/my 是 root 局部坐标
     * （已减去 root 居中偏移），但 fullscreen modal 子树渲染时使用屏幕坐标，
     * HitTester 进入 modal 子树命中需要把 mx/my 加回 rootOffset 才能对齐。
     *
     * 调用方必须在 dispatch* 调用结束后调 [clearRootOffset] 清理。
     */
    @JvmStatic
    fun setRootOffset(x: Float, y: Float) {
        HitTester.setRootOffset(x, y)
    }

    /** 清除 root 屏幕偏移；与 [setRootOffset] 配对调用。 */
    @JvmStatic
    fun clearRootOffset() {
        HitTester.clearRootOffset()
    }

    /**
     * 设置当前命中线程的全局 elementBox 查询函数（按 id 返回屏幕绝对坐标 float[4] 或 null）。
     *
     * 用途：spotlight modal 的目标元素跨 session（如引导 HUD spotlight 到另一个 HUD 的元素）时，
     * 命中侧需要能跨 session 查询屏幕绝对坐标以判断「洞内/洞外」。与渲染侧
     * `RenderContext.elementBox` 同口径。
     * 调用方必须在 dispatch* 结束后调 [clearElementBoxLookup] 清理。
     */
    @JvmStatic
    fun setElementBoxLookup(lookup: ((String) -> FloatArray?)?) {
        HitTester.setElementBoxLookup(lookup)
    }

    /** 清除 elementBox 查询函数；与 [setElementBoxLookup] 配对调用。 */
    @JvmStatic
    fun clearElementBoxLookup() {
        HitTester.clearElementBoxLookup()
    }

    /**
     * 分发鼠标点击。
     *
     * @param root   根元素
     * @param layout 布局结果（每个节点的盒相对其父节点）
     * @param mx     鼠标 X（与根节点同一坐标系）
     * @param my     鼠标 Y
     * @param button 按键编号
     * @param evalContext 可选求值上下文（[OverlayEvalContext] marker，实际为 EvalContext），用于 overlay 内容实例化共享屏幕 state
     * @return 是否被某个 handler 消费
     */
    @JvmStatic
    @JvmOverloads
    fun dispatchClick(
        root: UIElement<*>?,
        layout: LayoutResult?,
        mx: Double,
        my: Double,
        button: Int,
        evalContext: OverlayEvalContext? = null,
    ): Boolean {
        if (root == null || layout == null) {
            return false
        }
        return runCatching { dispatchClickInternal(root, layout, mx, my, button, evalContext) }
            .getOrElse { t ->
                // 结构性命中/折叠/特化分发抛异常已被隔离，避免冒泡到平台层 mouseClicked 致客户端崩溃。
                // 降级语义：返回未消费（false），由平台层走 vanilla 后续处理；记 ERROR 含按键定位。
                logDispatchFailure("dispatchClick", "button=$button, mx=$mx, my=$my", t)
                false
            }
    }

    @Suppress("LongMethod", "CyclomaticComplexMethod", "ReturnCount")
    private fun dispatchClickInternal(
        root: UIElement<*>,
        layout: LayoutResult,
        mx: Double,
        my: Double,
        button: Int,
        evalContext: OverlayEvalContext?,
    ): Boolean {
        markInput(currentTimeMillis())
        val rightClick = button == BUTTON_RIGHT
        if (!rightClick && overlayOrchestrator().dispatchOverlayClick(root, mx, my, button)) {
            return true
        }
        // 新一次按下开启全新捕获会话：先清空上一次残留（防御性，正常路径
        // 由平台层 mouseReleased → releaseDragCapture 清理；测试场景或异常
        // 路径下 release 可能丢失，这里兜底）
        releaseDragCapture()
        // 点击外部折叠：扫描整树，对所有展开态 Dropdown，若点击落在其扩展命中盒（含面板）外，
        // 则提前折叠。放在 hitTest 之前可保证本帧 hitTest 看到的是已折叠后的状态——
        // 命中头部 / 命中 option 时该 Dropdown 自身的扩展盒包含命中点不会被折叠。
        ControlInteractions.collapseDropdownsOnOutsideClick(root, layout, mx, my)
        // 收集从根到最深命中元素的路径
        val path = ArrayList<UIElement<*>>()
        val absBoxes = ArrayList<FloatArray>()
        if (!HitTester.hitDropdownOverlay(root, layout, mx, my, path, absBoxes) &&
            !HitTester.hit(root, layout, mx, my, path, absBoxes)
        ) {
            return false
        }
        // 禁用按钮拦截：最深命中元素为 disabled 或 loading 的 Button 时，不设置 pressed、不触发 handler、不开始 drag capture
        if (isDisabledButton(path.lastOrNull())) {
            return false
        }
        if (rightClick && overlayOrchestrator().activateRightClickMenu(root, path, absBoxes, mx, my, evalContext)) {
            return true
        }
        if (rightClick && overlayOrchestrator().dispatchOverlayClick(root, mx, my, button)) {
            return true
        }
        // 设置 press 状态并触发 press 动画（最深命中元素）
        path.lastOrNull()?.let { deepest ->
            deepest.pressed(true)
            DragCaptureManager.setPressedElement(deepest)
            // 若元素配置了 ripple，按命中点的元素相对坐标启动 ripple 动画
            startRippleIfConfigured(deepest, absBoxes, mx, my)
            // marker 断环，见 AnimationDriverHandle
            deepest.animationScheduler()?.let { scheduler ->
                scheduler.cancelTrigger(deepest, TRIGGER_IDLE)
                scheduler.playTrigger(deepest, TRIGGER_PRESS)
            }
        }
        // Slider 控件特化：先按命中位置写入 value，再让冒泡 onClick handler 触发上报 action
        ControlInteractions.applySliderInteraction(path, absBoxes, mx)
        // TextField 控件特化：命中 TextField 时按局部坐标定位光标（塌缩选区）。独立分支，
        // 不与 slider/button/scrollable 冒泡混淆；MouseEvent 无修饰位，故仅塌缩定位、不做 Shift+Click。
        // multiline 用 (X,Y) 选视觉行 + 行内列；单行仅按 X 定位（零回归）。
        ControlInteractions.applyTextFieldClick(path, absBoxes, mx, my)
        // 鼠标按下命中 Slider：捕获该元素，后续 drag 跳过 hit test 直发它，
        // 避免窄高 slider 拖动时鼠标偏离命中盒导致断触
        DragCaptureManager.captureDragIfSlider(path, absBoxes)
        // 命中 TextField 时捕获指针用于拖选：Slider 未捕获时才捕，仅对 TextField 生效。
        DragCaptureManager.captureDragIfTextField(path, absBoxes)
        // 修复「滚动条拖一点就卡住」:Slider 没捕获时,若命中链路里有 ScrollableContainer
        // 也做指针捕获,后续 drag 投递到该容器,鼠标拖出容器边界仍持续滚动;
        // mouseReleased 释放捕获时由 onDragEnd 触发惯性 / 弹性回弹。
        DragCaptureManager.captureDragIfScrollable(path, absBoxes)
        // Dropdown / TabView 特化：option 选择 + tab header 切换。命中时直接消费事件，不再走默认冒泡。
        if (ControlInteractions.applySpecialClick(path, absBoxes, mx, my)) {
            return true
        }
        // 子优先冒泡：从路径末端（最深）向根遍历，找到首个对应 handler 即触发
        val event = MouseEvent(mx, my, button)
        if (bubbleClick(path, absBoxes, event, mx, my, rightClick)) {
            return true
        }
        // 右键未命中专属 handler 时回退到 onClick（兼容旧行为）
        if (rightClick && bubbleClick(path, absBoxes, event, mx, my, false, "onClick(右键 fallback)")) {
            return true
        }
        // 仅声明长按/拖拽启动的元素也必须消费左键按下，否则平台层可能不继续派发 drag/release。
        return button == 0 && (hasDeferredGestureHandler(path) || findDragReorderHit(path) != null)
    }

    /** 子优先冒泡触发 click handler：rightClick 时取 onRightClickHandler，否则 onClickHandler。 */
    private fun bubbleClick(
        path: List<UIElement<*>>,
        absBoxes: List<FloatArray>,
        event: MouseEvent,
        mx: Double,
        my: Double,
        rightClick: Boolean,
        tag: String = if (rightClick) "onRightClick" else "onClick",
    ): Boolean {
        for (i in path.indices.reversed()) {
            val element = path[i]
            val handler = if (rightClick) element.onRightClickHandler() else element.onClickHandler()
            if (handler != null) {
                // 填充本地坐标：DSL lambda 用 event.localX / event.localY 拿元素 box 内坐标，
                // 避免手动减去屏幕偏移做 hitArc / hitRect 命中。
                fillLocalCoords(event, absBoxes, i, mx, my)
                invokeHandler(handler, event, tag, element)
                return true
            }
        }
        return false
    }

    /**
     * 直接转发键盘事件给当前焦点元素（不感知 modal 层）。
     *
     * 保留为向后兼容入口，返回值固定投影为 [KeyDispatchResult.handled]。
     */
    @JvmStatic
    fun dispatchKey(focused: UIElement<*>?, event: KeyEvent): Boolean = dispatchKeyResult(focused, event).handled

    /** 分发键盘事件并把兼容返回值固定投影为 [KeyDispatchResult.handled]。 */
    @JvmStatic
    fun dispatchKey(root: UIElement<*>?, focused: UIElement<*>?, event: KeyEvent?): Boolean =
        dispatchKeyResult(root, focused, event).handled

    /** 直接转发键盘事件给当前焦点元素，并分别返回处理与消费状态。 */
    @JvmStatic
    fun dispatchKeyResult(focused: UIElement<*>?, event: KeyEvent): KeyDispatchResult =
        dispatchKeyResult(null, focused, event)

    /**
     * 分发键盘事件，支持 overlay、modal 与焦点元素优先级，并分别返回处理与消费状态。
     */
    @JvmStatic
    fun dispatchKeyResult(root: UIElement<*>?, focused: UIElement<*>?, event: KeyEvent?): KeyDispatchResult {
        return try {
            val escPress = event != null && isEscPress(event)
            if (root != null && escPress && overlayOrchestrator().dismissTopOverlay(root)) {
                return KEY_HANDLED_AND_CONSUMED
            }
            val topModal = topVisibleModal(root)
            if (topModal != null && escPress) {
                return handleModalEsc(topModal)
            }
            forwardKeyToFocused(focused, event)
        } catch (t: Throwable) {
            // 键盘分发异常隔离：避免冒泡到平台层 keyPressed 致崩溃，降级为未处理且未消费。
            logDispatchFailure("dispatchKeyResult", "key=${event?.key() ?: "(null)"}", t)
            KEY_UNHANDLED
        }
    }

    /** 取顶层 visible modal（root 为空或无可见 modal 时返回 null）。 */
    private fun topVisibleModal(root: UIElement<*>?): top.wcpe.allincore.ui.runtime.element.Modal? {
        if (root == null) return null
        return overlayOrchestrator().collectVisibleModals(root).lastOrNull()
    }

    /** 仅 ESC 的 press / repeat（action != 0）才视作「按下」。 */
    private fun isEscPress(event: KeyEvent): Boolean = UiKeys.ESCAPE == event.key() && event.action() != ACTION_RELEASE

    /**
     * 顶层 modal 收到 ESC press：按 dismissOnEsc 决定是否触发 onClickHandler。非 passthrough modal 吸收事件。
     */
    private fun handleModalEsc(modal: top.wcpe.allincore.ui.runtime.element.Modal): KeyDispatchResult {
        if (modal.passthrough()) return KEY_UNHANDLED
        if (!modal.dismissOnEsc()) return KEY_HANDLED_AND_CONSUMED
        val handler = modal.onClickHandler() ?: return KEY_HANDLED_AND_CONSUMED
        invokeHandlerSafely(handler, MouseEvent(0.0, 0.0, 0), "onClick(modal ESC)", modal)
        return KEY_HANDLED_AND_CONSUMED
    }

    /** 把键盘事件交给焦点元素的 onKeyHandler，并分别返回处理与消费状态。 */
    private fun forwardKeyToFocused(focused: UIElement<*>?, event: KeyEvent?): KeyDispatchResult {
        val handler = focused?.onKeyHandler() ?: return KEY_UNHANDLED
        if (event == null) return KEY_UNHANDLED
        invokeHandlerSafely(handler, event, "onKey", focused)
        return KeyDispatchResult(handled = true, consumed = event.isConsumed)
    }

    /** 直接转发字符事件给当前焦点元素。 */
    @JvmStatic
    fun dispatchChar(focused: UIElement<*>?, event: CharEvent): Boolean {
        if (focused == null) {
            return false
        }
        return try {
            val handler = focused.onCharHandler() ?: return false
            invokeHandler(handler, event, "onChar", focused)
            true
        } catch (t: Throwable) {
            // 字符分发异常隔离：避免冒泡到平台层 charTyped 致崩溃。降级返回未消费（false）。
            logDispatchFailure("dispatchChar", "focused=${focused.id() ?: "(匿名)"}", t)
            false
        }
    }

    /** 分发鼠标拖拽：策略与 [dispatchClick] 相同（最深命中 + 子优先冒泡）。 */
    @JvmStatic
    fun dispatchDrag(
        root: UIElement<*>?,
        layout: LayoutResult?,
        mx: Double,
        my: Double,
        button: Int,
        dx: Double,
        dy: Double,
    ): Boolean {
        if (root == null || layout == null) {
            return false
        }
        return runCatching { dispatchDragInternal(root, layout, mx, my, button, dx, dy) }
            .getOrElse { t ->
                // 拖拽分发异常隔离：避免冒泡到平台层 mouseDragged 致崩溃。降级返回未消费（false）。
                logDispatchFailure("dispatchDrag", "button=$button, mx=$mx, my=$my", t)
                false
            }
    }

    @Suppress("ReturnCount")
    private fun dispatchDragInternal(
        root: UIElement<*>,
        layout: LayoutResult,
        mx: Double,
        my: Double,
        button: Int,
        dx: Double,
        dy: Double,
    ): Boolean {
        // 鼠标捕获：若按下时命中了 Slider 或 ScrollableContainer 且其布局仍在当前 layout 中存在，
        // 整个 drag 序列都直接投递给它，不再做 hit test。这是 GUI 标准做法，
        // 让窄控件拖动稳定不断触。layout.boxOf 检查兼顾跨 root / screen 切换
        // 场景下的捕获自动失效。
        val captureTarget = DragCaptureManager.getDragCaptureTarget()
        val captureAbsBox = DragCaptureManager.getDragCaptureAbsBox()
        if (captureTarget != null && layout.boxOf(captureTarget) != null) {
            when {
                captureTarget is SliderInputTarget && captureAbsBox != null -> {
                    // 直接把 ratio 喂给被捕获的 slider（mx 越界也无妨，setValueFromRatio 内部 clamp）
                    DragCaptureManager.applySliderInteraction(mx)
                    deliverDrag(captureTarget, mx, my, button, dx, dy, "onDrag(slider 捕获)")
                    return true
                }
                // TextField 拖选：被捕获 TextField 时，按局部坐标扩展选区（extend=true），鼠标拖出命中盒仍持续扩选。
                // multiline 用 (X,Y) 命中，单行仅按 X（localY 被忽略）。
                captureTarget is TextFieldInputTarget && captureAbsBox != null -> {
                    captureTarget.setCaretFromLocal(mx - captureAbsBox[0], my - captureAbsBox[1], true)
                    deliverDrag(captureTarget, mx, my, button, dx, dy, "onDrag(textfield 拖选)")
                    return true
                }
                // 滚动容器捕获:鼠标移到任何位置,deltaY/deltaX 都喂给目标容器的 onDrag,
                // 修复「拖出容器边界即卡住」的历史问题。捕获释放(mouseReleased)由
                // DragCaptureManager.releaseDragCapture 通知 onDragEnd 触发惯性 / 弹性。
                captureTarget is ScrollableContainer<*> -> {
                    deliverDrag(captureTarget, mx, my, button, dx, dy, "onDrag(scroll 捕获)")
                    return true
                }
            }
        }
        val path = ArrayList<UIElement<*>>()
        val absBoxes = ArrayList<FloatArray>()
        if (!HitTester.hit(root, layout, mx, my, path, absBoxes)) {
            return false
        }
        // Slider 控件特化：拖拽过程中按鼠标 X 实时更新 value
        ControlInteractions.applySliderInteraction(path, absBoxes, mx)
        val event = DragEvent(mx, my, button, dx, dy)
        for (i in path.indices.reversed()) {
            val element = path[i]
            val handler = element.onDragHandler()
            if (handler != null) {
                invokeHandler(handler, event, "onDrag", element)
                return true
            }
        }
        return false
    }

    /** 向被捕获目标投递一次 onDrag 事件（无 handler 时静默）。 */
    private fun deliverDrag(
        target: UIElement<*>,
        mx: Double,
        my: Double,
        button: Int,
        dx: Double,
        dy: Double,
        tag: String,
    ) {
        target.onDragHandler()?.let { handler ->
            invokeHandler(handler, DragEvent(mx, my, button, dx, dy), tag, target)
        }
    }

    /**
     * 分发鼠标悬停事件：命中路径上首个 onHoverHandler 触发。
     *
     * 由平台层在鼠标进入新元素时调用（非每帧调用）。
     */
    @JvmStatic
    fun dispatchHover(root: UIElement<*>?, layout: LayoutResult?, mx: Double, my: Double): Boolean =
        bubbleMouseHandler(root, layout, mx, my, "onHover") { it.onHoverHandler() }

    /**
     * 分发长按事件：命中路径上首个 onLongPressHandler 触发。
     *
     * 第六阶段不内建按压时长 / 抖动阈值状态机；长按手势由平台层识别后通过本入口投递。
     */
    @JvmStatic
    @JvmOverloads
    @Suppress("ReturnCount")
    fun dispatchLongPress(
        root: UIElement<*>?,
        layout: LayoutResult?,
        mx: Double,
        my: Double,
        evalContext: OverlayEvalContext? = null,
    ): Boolean {
        if (root == null || layout == null) return false
        val path = ArrayList<UIElement<*>>()
        val absBoxes = ArrayList<FloatArray>()
        if (!HitTester.hit(root, layout, mx, my, path, absBoxes)) return false
        if (isDisabledButton(path.lastOrNull())) {
            return false
        }
        if (overlayOrchestrator().activateContextMenu(root, path, absBoxes, mx, my, evalContext)) return true
        if (startDragReorderIfHit(path, mx, my)) return true
        // Phase 4 全局拖拽:长按命中链上首个声明 onDragStart 的元素 → 启动拖拽会话。
        // 与 onLongPress 互斥(拖拽优先);payload 由 onDragStart handler 通过 startDrag 语义自定。
        for (i in path.indices.reversed()) {
            val element = path[i]
            if (element.onDragStartHandler() != null) {
                startDrag(element, element.id(), mx, my)
                return true
            }
        }
        val event = MouseEvent(mx, my, 0)
        for (i in path.indices.reversed()) {
            val element = path[i]
            val handler = element.onLongPressHandler()
            if (handler != null) {
                invokeHandler(handler, event, "onLongPress", element)
                return true
            }
        }
        return false
    }

    /**
     * v4 A.1：按 hitTest 路径自顶向下找首个声明 contextMenuTriggerMs > 0 的元素，
     * 返回该值；未命中或所有元素未声明返回 [UIElement.CONTEXT_MENU_TRIGGER_MS_UNSET]。
     * 平台层据此决定有效长按阈值，哨兵值时由调用方回退到主题 longPressThresholdMs。
     *
     * 独立于 dispatchLongPress：阈值决策发生在 LongPressTracker tick 之前（用最近一次 press 坐标），
     * dispatch 发生在 tracker 触发之后（用真触发坐标），二者解耦。
     */
    @JvmStatic
    fun resolveContextMenuTriggerMs(root: UIElement<*>?, layout: LayoutResult?, mx: Double, my: Double): Long {
        if (root == null || layout == null) return UIElement.CONTEXT_MENU_TRIGGER_MS_UNSET
        val path = ArrayList<UIElement<*>>()
        val absBoxes = ArrayList<FloatArray>()
        if (!HitTester.hit(root, layout, mx, my, path, absBoxes)) return UIElement.CONTEXT_MENU_TRIGGER_MS_UNSET
        // v4 §A.1：「自顶向下」→ root 起头方向遍历（HitTester.hit 写入顺序为 root → 命中元素）
        for (element in path) {
            val ms = element.contextMenuTriggerMs()
            if (ms > UIElement.CONTEXT_MENU_TRIGGER_MS_UNSET) return ms
        }
        return UIElement.CONTEXT_MENU_TRIGGER_MS_UNSET
    }

    /**
     * 分发双击事件：命中路径上最深的 Button 若配置了 onDoubleClickHandler 则触发；
     * 命中启用的 TextField 时选中双击位置所在单词（独立分支，不影响 Button 双击语义）。
     */
    @JvmStatic
    @Suppress("ReturnCount")
    fun dispatchDoubleClick(root: UIElement<*>?, layout: LayoutResult?, mx: Double, my: Double, button: Int): Boolean {
        if (root == null || layout == null) return false
        // TextField 双击选词：需要命中元素的 absBox 以换算局部 X，故走带 absBoxes 的命中。
        val path = ArrayList<UIElement<*>>()
        val absBoxes = ArrayList<FloatArray>()
        if (!HitTester.hit(root, layout, mx, my, path, absBoxes)) return false
        val element = path.lastOrNull()
        if (element is TextFieldInputTarget && element.enabled()) {
            val absBox = absBoxes.last()
            // multiline 用 (X,Y) 选视觉行 + 行内列；单行仅按 X（localY 被忽略）
            element.selectWordAtLocal(mx - absBox[0], my - absBox[1])
            return true
        }
        if (element !is Button || element.loading() || !element.enabled()) {
            return false
        }
        val handler = element.onDoubleClickHandler() ?: return false
        invokeHandler(handler, MouseEvent(mx, my, button), "onDoubleClick", element)
        return true
    }

    /** 分发拖拽进入事件（桩方法）：命中路径上首个 onDragEnterHandler 触发。 */
    @JvmStatic
    fun dispatchDragEnter(root: UIElement<*>?, layout: LayoutResult?, mx: Double, my: Double): Boolean =
        bubbleMouseHandler(root, layout, mx, my, "onDragEnter") { it.onDragEnterHandler() }

    /** 分发拖拽离开事件（桩方法）：命中路径上首个 onDragLeaveHandler 触发。 */
    @JvmStatic
    fun dispatchDragLeave(root: UIElement<*>?, layout: LayoutResult?, mx: Double, my: Double): Boolean =
        bubbleMouseHandler(root, layout, mx, my, "onDragLeave") { it.onDragLeaveHandler() }

    /**
     * 分发鼠标滚轮：嵌套滚动协议（Phase 4）。
     *
     * 由最深命中元素向父冒泡：每个 onScroll handler 处理后，若事件
     * `ScrollEvent.fullyConsumed` 则停止；否则把剩余滚动量交给父容器。
     * 滚动容器在未到边界时会完整消费（行为同改造前），到边界后剩余量上交父。
     */
    @JvmStatic
    fun dispatchScroll(
        root: UIElement<*>?,
        layout: LayoutResult?,
        mx: Double,
        my: Double,
        sx: Double,
        sy: Double,
    ): Boolean = dispatchScrollResult(root, layout, mx, my, sx, sy).handled

    /** 分发滚轮并仅在滚动量被完整消费时返回 true，供需要决定平台默认行为的适配器使用。 */
    @JvmStatic
    fun dispatchScrollConsumed(
        root: UIElement<*>?,
        layout: LayoutResult?,
        mx: Double,
        my: Double,
        sx: Double,
        sy: Double,
    ): Boolean = dispatchScrollResult(root, layout, mx, my, sx, sy).consumed

    private fun dispatchScrollResult(
        root: UIElement<*>?,
        layout: LayoutResult?,
        mx: Double,
        my: Double,
        sx: Double,
        sy: Double,
    ): ScrollDispatchResult {
        if (root == null || layout == null) return ScrollDispatchResult.NONE
        return try {
            val path = ArrayList<UIElement<*>>()
            if (!HitTester.hit(root, layout, mx, my, path, null)) return ScrollDispatchResult.NONE
            val event = ScrollEvent(mx, my, sx, sy)
            var handled = false
            for (i in path.indices.reversed()) {
                val handler = path[i].onScrollHandler() ?: continue
                handled = true
                invokeHandler(handler, event, "onScroll", path[i])
                if (event.fullyConsumed()) break
            }
            ScrollDispatchResult(handled, event.fullyConsumed())
        } catch (t: Throwable) {
            // 滚轮分发异常隔离：避免冒泡到平台层 mouseScrolled 致崩溃。降级返回未消费。
            logDispatchFailure("dispatchScroll", "mx=$mx, my=$my, sy=$sy", t)
            ScrollDispatchResult.NONE
        }
    }

    private data class ScrollDispatchResult(val handled: Boolean, val consumed: Boolean) {
        companion object {
            val NONE = ScrollDispatchResult(handled = false, consumed = false)
        }
    }

    // ===== Phase 4：全局拖拽 DragSession =====

    /**
     * 启动一个全局拖拽会话:源元素 sourceId 携带 payload 开始拖拽。
     *
     * 触发源元素 onDragStart handler。坐标用屏幕坐标(与 mouseReleased/ghost 渲染口径一致)。
     */
    @JvmStatic
    fun startDrag(source: UIElement<*>?, payload: Any?, mx: Double, my: Double): DragSession? {
        if (source == null) {
            return null
        }
        val session = DragSession.start(source.id().orEmpty(), payload, mx, my)
        source.onDragStartHandler()?.let { handler ->
            invokeHandler(handler, DragStartEvent(source.id().orEmpty(), payload, mx, my), "onDragStart", source)
        }
        return session
    }

    /**
     * 拖拽移动:命中当前坐标下首个 dropTarget,切换 currentTarget 并派发 onDragOver/onDragLeave。
     *
     * 仅在有活跃会话时介入;无会话时为 no-op,不影响普通鼠标移动。
     *
     * @return 是否有活跃拖拽会话
     */
    @JvmStatic
    fun dispatchDragMove(root: UIElement<*>?, layout: LayoutResult?, mx: Double, my: Double): Boolean {
        val session = DragSession.current()
        if (session == null || root == null || layout == null) {
            return false
        }
        session.updateMouse(mx, my)
        val newTarget = findDropTarget(root, layout, mx, my)
        val oldTarget = session.currentTarget
        if (newTarget !== oldTarget) {
            // 离开旧 target
            oldTarget?.onDragLeaveHandler()?.let { handler ->
                invokeHandler(handler, MouseEvent(mx, my, 0), "onDragLeave", oldTarget)
            }
            session.currentTarget(newTarget)
        }
        // 悬停 onDragOver(每次移动都发,业务可据此更新插入指示)
        newTarget?.onDragOverHandler()?.let { handler ->
            invokeHandler(handler, DragOverEvent(session.sourceId, session.payload, mx, my), "onDragOver", newTarget)
        }
        return true
    }

    /**
     * 拖拽释放:若当前命中 dropTarget 则派发 onDrop,否则视为取消;无论如何结束会话。
     *
     * @return 是否有活跃拖拽会话被处理(true 表示消费了本次 release)
     */
    @JvmStatic
    fun dispatchDrop(root: UIElement<*>?, layout: LayoutResult?, mx: Double, my: Double): Boolean {
        val session = DragSession.current() ?: return false
        if (dropDragReorderIfHit(root, layout, mx, my, session)) {
            DragSession.end()
            return true
        }
        val target = if (root != null && layout != null) findDropTarget(root, layout, mx, my) else null
        target?.onDropHandler()?.let { handler ->
            val dropEvent = DropEvent(session.sourceId, target.id().orEmpty(), session.payload, mx, my)
            invokeHandler(handler, dropEvent, "onDrop", target)
        }
        DragSession.end()
        return true
    }

    /** 命中坐标下最深的、标记为 dropTarget 的元素;无则返回 null。 */
    private fun findDropTarget(root: UIElement<*>, layout: LayoutResult, mx: Double, my: Double): UIElement<*>? {
        val path = ArrayList<UIElement<*>>()
        if (!HitTester.hit(root, layout, mx, my, path, null)) {
            return null
        }
        for (i in path.indices.reversed()) {
            if (path[i].dropTarget()) {
                return path[i]
            }
        }
        return null
    }

    /** 命中 DragReorder 行时，以行 index 作为 payload 启动排序拖拽。 */
    private fun startDragReorderIfHit(path: List<UIElement<*>>, mx: Double, my: Double): Boolean {
        val hit = findDragReorderHit(path) ?: return false
        hit.dragReorder.startReorderDrag(hit.index, hit.item, mx, my) ?: return true
        val eventSource = deepestDragStartElement(path) ?: hit.item
        eventSource.onDragStartHandler()?.let { handler ->
            invokeHandler(
                handler,
                DragStartEvent(eventSource.id().orEmpty(), hit.index, mx, my),
                "onDragStart(dragReorder)",
                eventSource,
            )
        }
        return true
    }

    /** DragReorder 会话松手命中目标行时，转成 onReorder(from,to)。 */
    private fun dropDragReorderIfHit(
        root: UIElement<*>?,
        layout: LayoutResult?,
        mx: Double,
        my: Double,
        session: DragSession,
    ): Boolean {
        if (root == null || layout == null) return false
        val payload = session.payload as? DragReorder.ReorderPayload ?: return false
        val path = ArrayList<UIElement<*>>()
        if (!HitTester.hit(root, layout, mx, my, path, null)) {
            return false
        }
        val hit = findDragReorderHit(path) ?: return false
        if (!sameId(hit.dragReorder.id(), payload.reorderId)) {
            return false
        }
        hit.dragReorder.handleDrop(hit.index)
        return true
    }

    /** 解析当前命中路径是否位于 DragReorder 的可排序窗口子项内。 */
    private fun findDragReorderHit(path: List<UIElement<*>>): DragReorderHit? {
        for (i in path.indices.reversed()) {
            val dragReorder = path[i]
            if (dragReorder !is DragReorder) {
                continue
            }
            if (!dragHandleAllowed(dragReorder, path, i)) {
                return null
            }
            return when (val body = dragReorder.body()) {
                is VirtualList -> hitVirtualListItem(dragReorder, body, path)
                is VirtualGrid -> hitVirtualGridItem(dragReorder, body, path)
                else -> null
            }
        }
        return null
    }

    private fun hitVirtualListItem(
        dragReorder: DragReorder,
        list: VirtualList,
        path: List<UIElement<*>>,
    ): DragReorderHit? {
        val hit = hitWindowChild(path, list.children()) ?: return null
        return DragReorderHit(dragReorder, list.firstVisible() + hit.position, hit.child)
    }

    private fun hitVirtualGridItem(
        dragReorder: DragReorder,
        grid: VirtualGrid,
        path: List<UIElement<*>>,
    ): DragReorderHit? {
        val hit = hitWindowChild(path, grid.children()) ?: return null
        return DragReorderHit(dragReorder, grid.firstVisible() + hit.position, hit.child)
    }

    /** 找出命中路径中属于窗口容器的直接子项。 */
    private fun hitWindowChild(path: List<UIElement<*>>, windowChildren: List<UIElement<*>>): WindowChildHit? {
        for (element in path) {
            val idx = windowChildren.indexOfFirst { it === element }
            if (idx >= 0) {
                return WindowChildHit(idx, element)
            }
        }
        return null
    }

    private fun dragHandleAllowed(dragReorder: DragReorder, path: List<UIElement<*>>, dragReorderIndex: Int): Boolean {
        val handleId = dragReorder.dragHandleId()
        if (handleId.isNullOrEmpty()) {
            return true
        }
        for (i in dragReorderIndex + 1 until path.size) {
            if (handleId == path[i].id()) {
                return true
            }
        }
        return false
    }

    private fun deepestDragStartElement(path: List<UIElement<*>>): UIElement<*>? {
        for (i in path.indices.reversed()) {
            if (path[i].onDragStartHandler() != null) {
                return path[i]
            }
        }
        return null
    }

    private fun hasDeferredGestureHandler(path: List<UIElement<*>>): Boolean =
        path.any { it.onDragStartHandler() != null || it.onLongPressHandler() != null }

    private fun sameId(left: String?, right: String?): Boolean = left != null && left == right

    private data class DragReorderHit(val dragReorder: DragReorder, val index: Int, val item: UIElement<*>)

    private data class WindowChildHit(val position: Int, val child: UIElement<*>)

    /**
     * v4 C.2：分发指针移动事件——命中链上首个 [UIElement.onPointerMoveHandler] 触发。
     *
     * 与 [dispatchHover] 的区别：
     * - [dispatchHover] 由平台层在「鼠标进入新元素」时调用一次（语义偏离散）；
     * - 本方法每次 [dispatchMouseMove] 期间都会被调用，频率更高，
     *   适合需要持续追踪坐标的交互（如 Knob/ArcMenu 命中态、自定义 Tooltip 偏移等）。
     *
     * 调用方一般不直接调本方法，而是依赖 [dispatchMouseMove] 内部串入的调用；
     * 暴露为 public 仅用于平台层与单元测试单独验证 pointer-move 链路。
     */
    @JvmStatic
    fun dispatchPointerMove(root: UIElement<*>?, layout: LayoutResult?, mx: Double, my: Double): Boolean =
        bubbleMouseHandler(root, layout, mx, my, "onPointerMove") { it.onPointerMoveHandler() }

    /**
     * 命中后子优先冒泡触发首个 MouseEvent 型 handler 的公共骨架。
     * 供 hover / dragEnter / dragLeave / pointerMove 等无 absBox 需求的离散事件复用。
     */
    private inline fun bubbleMouseHandler(
        root: UIElement<*>?,
        layout: LayoutResult?,
        mx: Double,
        my: Double,
        tag: String,
        handlerOf: (UIElement<*>) -> UiEventHandler<MouseEvent>?,
    ): Boolean {
        if (root == null || layout == null) return false
        val path = ArrayList<UIElement<*>>()
        if (!HitTester.hit(root, layout, mx, my, path, null)) return false
        val event = MouseEvent(mx, my, 0)
        for (i in path.indices.reversed()) {
            val handler = handlerOf(path[i])
            if (handler != null) {
                invokeHandler(handler, event, tag, path[i])
                return true
            }
        }
        return false
    }

    /**
     * 分发鼠标移动：仅刷新元素 `hovered` 视觉状态——命中路径上的元素置 true，其余元素置 false。不触发 handler。
     *
     * TODO（Phase 7b）：当前实现每次移动都会递归整棵元素树写 hovered 状态；
     * 元素数 < 100 时无感，但 ScrollView/ListView 落地后元素规模变大需改为增量 diff。
     */
    @JvmStatic
    fun dispatchMouseMove(root: UIElement<*>?, layout: LayoutResult?, mx: Double, my: Double) {
        if (root == null || layout == null) {
            return
        }
        try {
            dispatchMouseMoveInternal(root, layout, mx, my)
        } catch (t: Throwable) {
            // 鼠标移动分发异常隔离：避免冒泡到平台层 mouseMoved 致崩溃。降级语义：本帧 hover/cursor 刷新跳过。
            logDispatchFailure("dispatchMouseMove", "mx=$mx, my=$my", t)
        }
    }

    private fun dispatchMouseMoveInternal(root: UIElement<*>, layout: LayoutResult, mx: Double, my: Double) {
        markInput(currentTimeMillis())
        // 优先检查 overlay 内容树的 hover
        if (overlayOrchestrator().dispatchOverlayMouseMove(root, mx, my)) {
            // 鼠标在 overlay 内，主树全部取消 hover
            applyHoverState(root, emptyList())
            // v4 C.1：主树所有 CanvasElement 视为未命中，仅刷 inside=false 保留最后坐标
            applyCanvasMouseState(root, emptyList(), emptyList(), mx, my)
            // 鼠标落在浮层上：主树 cursor 复位为默认箭头，避免卡在悬停时设过的手型等形状
            applyCursor(emptyList())
            return
        }
        val path = ArrayList<UIElement<*>>()
        val absBoxes = ArrayList<FloatArray>()
        if (!HitTester.hitDropdownOverlay(root, layout, mx, my, path, absBoxes)) {
            HitTester.hit(root, layout, mx, my, path, absBoxes)
        }
        // 用集合代替 List.contains 提速：路径通常很短，直接遍历也足够
        applyHoverState(root, path)
        // 从命中链末端向前找首个声明了 cursor 的元素，解析为 CursorType 上报平台；
        // 未命中任何元素时复位为默认箭头（path 为空 → DEFAULT）
        applyCursor(path)
        // v4 C.1：把鼠标坐标写入命中链中的 CanvasElement，并把树中其余 CanvasElement 标记为未命中
        applyCanvasMouseState(root, path, absBoxes, mx, my)
        // v4 C.2：mouse-move 同时触发命中链上首个 onPointerMoveHandler（若有）。
        // 与 hover 状态更新并行——hover 是元素视觉状态，pointerMove 是 DSL 事件回调，二者解耦。
        dispatchPointerMove(root, layout, mx, my)
    }

    /** 命中测试：返回当前坐标命中的最深元素，未命中返回 null。 */
    @JvmStatic
    fun hitElement(root: UIElement<*>?, layout: LayoutResult?, mx: Double, my: Double): UIElement<*>? {
        if (root == null || layout == null) {
            return null
        }
        val path = ArrayList<UIElement<*>>()
        if (!HitTester.hit(root, layout, mx, my, path, null)) {
            return null
        }
        return path.lastOrNull()
    }

    /**
     * 命中链查询：返回从根到命中节点沿途经过的所有节点 id（顺序为 root → 命中节点）。
     *
     * 仅收集 [UIElement.id] 非空的节点 id，过滤掉匿名节点；未命中返回空列表。
     * 供 dev-debug overlay 上报命中链使用（[HitTester] 为包私有，平台层无法直接拿到 path，
     * 故由本公开门面方法转出）。
     */
    @JvmStatic
    fun hitChainIds(root: UIElement<*>?, layout: LayoutResult?, mx: Double, my: Double): List<String> {
        if (root == null || layout == null) {
            return emptyList()
        }
        val path = ArrayList<UIElement<*>>()
        if (!HitTester.hit(root, layout, mx, my, path, null)) {
            return emptyList()
        }
        return path.mapNotNull { it.id()?.takeIf(String::isNotEmpty) }
    }

    /**
     * 槽位命中测试：返回当前坐标命中的最深 [Slot] 元素，未命中返回 null。
     *
     * 用于平台层（如 fabric UIRuntimeScreen）将鼠标 / 键盘事件转发到 SlotEventBridge。
     */
    @JvmStatic
    fun hitSlot(root: UIElement<*>?, layout: LayoutResult?, mx: Double, my: Double): Slot? =
        hitElement(root, layout, mx, my) as? Slot

    /**
     * hover/tooltip 遮挡判定：鼠标是否被「比主树元素更靠前的浮层」遮挡。
     *
     * [hitElement]/[hitSlot] 只走主树 + modal 层（`HitTester.hit`），并未计入
     * 展开态 Dropdown 弹出面板与激活的 menu/context/interactive-tooltip overlay——这些浮层在主树之上，
     * 但命中测试看不到它们。平台层 tooltip pass 在弹 tooltip 前调用本方法，鼠标落在这些浮层之上时直接
     * 跳过，避免 tooltip 穿透下拉框 / 菜单等浮层（与点击派发 [dispatchMouseMove] 的遮挡顺序一致）。
     *
     * 坐标系：root 局部坐标（已减去屏幕居中偏移），与 [hitSlot] 一致。屏幕级 toast 为屏幕坐标，
     * 不在此判定，由平台层用 `ToastLayer.isPointOverToast` 单独判。modal 已由 hitSlot 内的 modal 层覆盖。
     */
    @JvmStatic
    fun isHoverBlockedByOverlay(root: UIElement<*>?, layout: LayoutResult?, mx: Double, my: Double): Boolean {
        if (root == null || layout == null) {
            return false
        }
        // 激活的 menu / context-menu / interactive tooltip 浮层
        if (overlayOrchestrator().isPointInActiveOverlay(root, mx, my)) {
            return true
        }
        // 展开态 Dropdown 的弹出面板
        return HitTester.hitDropdownOverlay(root, layout, mx, my, ArrayList(), ArrayList())
    }

    /** 若元素 ripple 字段有效，按命中点的元素相对坐标启动 ripple 动画。 */
    private fun startRippleIfConfigured(deepest: UIElement<*>, absBoxes: List<FloatArray>, mx: Double, my: Double) {
        val visual = deepest.style().visual()
        // 历史上曾要求 rippleDuration > 0；但 RippleAnimator.start 已固定走 RIPPLE_DURATION_MS，
        // duration 字段实际不参与动画时长控制。此处不再用 duration 作为是否启用的开关，
        // 改为仅当 radius / color 有效时启动 —— 否则 DSL 用户在 effects { ripple { color/radius } }
        // 不显式写 duration 的常见写法会被静默吞掉。
        if (visual.rippleRadius() <= 0f) return
        if (visual.rippleColor() ushr 24 == 0) return
        val absBox = absBoxes.lastOrNull() ?: return
        if (absBox.size < 4) return
        val relX = (mx - absBox[0]).toFloat()
        val relY = (my - absBox[1]).toFloat()
        RippleAnimator.start(deepest, relX, relY)
    }

    /**
     * 从命中链末端向前找首个声明了 `cursor` 的元素，解析为 [CursorType]
     * 经光标缝 [cursorSink] 上报平台层。命中链无 cursor 声明或链为空时上报
     * [CursorType.DEFAULT]，保证移出 / 落在浮层时光标复位为默认箭头。
     *
     * 去抖（同值不重复设置）由平台 CursorBridge 实现自行处理；此处只负责解析。
     * 未注入实现时 holder 返回 NOOP，零影响（与历史从不调用 setCursor 一致）。
     */
    private fun applyCursor(path: List<UIElement<*>>) {
        var type = CursorType.DEFAULT
        for (i in path.indices.reversed()) {
            val token = path[i].cursor()
            if (!token.isNullOrEmpty()) {
                type = CursorType.fromToken(token)
                break
            }
        }
        cursorSink().setCursor(type)
    }

    /**
     * 递归把命中路径上的元素 hovered=true，其他全部 false。
     *
     * 检测 hover 状态变化并触发 hover/leave 动画。
     * 注意：命中测试会把整条「根 → 最深命中」链都写入 `path`，
     * 因此命中子元素时祖先也会一并 hovered=true，让容器 tooltip 在子元素无 tooltip 时正确显示。
     */
    private fun applyHoverState(node: UIElement<*>, path: List<UIElement<*>>) {
        val wasHovered = node.hovered()
        val nowHovered = path.contains(node)
        if (wasHovered != nowHovered) {
            node.hovered(nowHovered)
            // marker 断环，见 AnimationDriverHandle
            node.animationScheduler()?.let { scheduler ->
                if (nowHovered) {
                    // 进入：触发 hover 动画，取消 idle 动画
                    scheduler.cancelTrigger(node, TRIGGER_IDLE)
                    scheduler.playTrigger(node, TRIGGER_HOVER)
                } else {
                    // 离开：触发 leave 动画
                    scheduler.playTrigger(node, TRIGGER_LEAVE)
                }
            }
        }
        // 遍历前对 children 取快照：翻转 hovered / 播放 hover 动画可能同步触发数据驱动容器
        // （$cond / $for / data-driven）的响应式 Effect 重建并 replaceChildren，直接迭代
        // live children 会抛 ConcurrentModificationException（与 UIRenderer.childrenSnapshot 同理）。
        for (child in node.children().toList()) {
            applyHoverState(child, path)
        }
    }

    /**
     * v4 C.1：刷新整棵元素树中所有 canvas 元素（[CanvasInputTarget]）的鼠标坐标缓存。
     *
     * 策略（spec §4.4 进一步简化方案）：
     * - 若 canvas 在 `path` 中：从 `absBoxes` 同序号取出屏幕绝对盒，
     *   写入 `(mx - absBox[0], my - absBox[1], inside=true)`；
     * - 若 canvas 不在 `path` 中：保留其上次缓存的坐标，仅把 inside 置为 false——
     *   供 DSL 退出动画继续读到坐标但条件式可识别「已离开」。
     *
     * 线程契约：与 [dispatchMouseMove] 同线程（UI 主线程）。
     *
     * @param node     当前递归节点（首次调用传 root）
     * @param path     `HitTester.hit` 写出的命中链；空列表表示无命中
     * @param absBoxes `path` 同序号的屏幕绝对盒（`float[4] = [x,y,w,h]`）；
     *                 长度可能小于 path（旧调用方未填）；越界视作「无 absBox」
     * @param mx       屏幕坐标系下的鼠标 X
     * @param my       屏幕坐标系下的鼠标 Y
     */
    private fun applyCanvasMouseState(
        node: UIElement<*>,
        path: List<UIElement<*>>,
        absBoxes: List<FloatArray>,
        mx: Double,
        my: Double,
    ) {
        if (node is CanvasInputTarget) {
            val idx = path.indexOf(node)
            val absBox = absBoxes.getOrNull(idx)
            if (idx >= 0 && absBox != null && absBox.size >= 2) {
                node.mousePosition(mx - absBox[0], my - absBox[1], true)
            } else {
                // 不在命中链中：仅刷 inside=false，保留最后坐标
                node.mousePosition(node.lastMouseX(), node.lastMouseY(), false)
            }
        }
        // 同 applyHoverState：遍历前取快照，避免遍历期间响应式重建 replaceChildren 修改
        // live children 抛 ConcurrentModificationException。
        for (child in node.children().toList()) {
            applyCanvasMouseState(child, path, absBoxes, mx, my)
        }
    }

    // === Stage 5：焦点保护查询 ===

    /**
     * 判断当前焦点元素是否为文本输入控件（如 TextField）。
     *
     * 平台层在处理 hotkey 前调用此方法：若返回 true，则所有 button hotkey 一律短路，
     * 避免用户在输入框中打字时误触快捷键。
     *
     * @param focusManager 当前 FocusManager 实例
     * @return 焦点元素为文本输入控件时返回 true
     */
    @JvmStatic
    fun isTextInputFocused(focusManager: FocusManager?): Boolean = focusManager?.current() is TextFieldInputTarget

    /**
     * 把 MouseEvent 的 localX / localY 设置为相对于命中元素 box 左上角的坐标。
     *
     * `absBoxes[i]` 由 [HitTester] 在命中链路上同步收集（每层元素在屏幕坐标系下的
     * 绝对 box，4 元素 float[]：{ left, top, right, bottom }）。本地坐标 = 屏幕坐标 − box.left/top。
     * DSL lambda 用 `event.localX` 直接得到 canvas / box 内位置，可直接喂 hitArc / hitRect
     * 命中判定，避免手动减 box 屏幕偏移。
     */
    private fun fillLocalCoords(event: MouseEvent, absBoxes: List<FloatArray>?, idx: Int, mx: Double, my: Double) {
        val box = absBoxes?.getOrNull(idx)
        if (box != null && box.size >= 2) {
            event.setLocal(mx - box[0], my - box[1])
            return
        }
        // 退化：缺 absBox 时本地坐标 = 屏幕坐标
        event.setLocal(mx, my)
    }

    /** 最深命中元素是 disabled / loading 的 Button 时返回 true（拦截 press / 触发 / drag capture）。 */
    private fun isDisabledButton(deepest: UIElement<*>?): Boolean =
        deepest is Button && (deepest.loading() || !deepest.enabled())

    /**
     * 统一记录 dispatch 公共入口的兜底异常：避免结构性遍历异常冒泡到 MC 主循环（mouseClicked/keyPressed
     * 等）导致整客户端崩溃。无条件记一次 ERROR（含入口名 + 关键事件上下文）；dev-debug verbose 时附完整堆栈。
     *
     * @param entry   分发入口名（如 "dispatchClick"），用于定位
     * @param context 关键事件上下文（如按键 / 键位 / 坐标），不含敏感数据
     * @param t       被隔离的异常
     */
    private fun logDispatchFailure(entry: String, context: String, t: Throwable) {
        if (uiVerbose()) {
            LOGGER.error("[ui] 输入分发入口 $entry 抛出异常，已隔离以保护 MC 主循环，上下文：$context", t)
        } else {
            LOGGER.error("[ui] 输入分发入口 $entry 抛出异常，已隔离以保护 MC 主循环，上下文：$context（开启 dev-debug 可看堆栈）")
        }
    }

    /**
     * 安全调用 handler.accept(event)：捕获并记录任何异常，避免单条 handler 崩溃影响后续清理。
     *
     * 典型场景：DSL onClick 内调用 local.openScreen("不存在的 screen") 抛 IllegalStateException，
     * 直接 propagate 出 dispatchClick 会让 fabric mouseClicked 收到异常，导致后续 mouseReleased
     * 不会被正常派发回旧 screen，进而 pressedElement 无法释放。包一层 try/catch 让 dispatcher
     * 始终走完正常流程，由后续 mouseReleased / removed() 完成状态清理。
     *
     * 暴露为 public 以便 [FocusManager] / [DragCaptureManager] 等同包之外的内部模块复用同一套异常吞拌策略。
     * 第三方调用方一般不需要，但保留为公共 API 不影响兼容性。
     *
     * @param handler 待调用 handler，调用方需保证非 null
     * @param event   传给 handler 的事件
     * @param tag     用于诊断日志的语义标签（如 "onClick"、"onLongPress"）
     * @param target  事件目标元素，仅用于日志定位
     */
    @JvmStatic
    fun <E> invokeHandler(handler: UiEventHandler<E>, event: E, tag: String, target: UIElement<*>?) {
        invokeHandlerSafely(handler, event, tag, target)
    }

    /** 安全调用处理器，成功返回 true，异常时记录并返回 false。 */
    private fun <E> invokeHandlerSafely(
        handler: UiEventHandler<E>,
        event: E,
        tag: String,
        target: UIElement<*>?,
    ): Boolean = try {
        handler.accept(event)
        true
    } catch (t: Throwable) {
        // 事件 handler 抛异常已被吞掉以保持 dispatcher 一致性。生产默认静默吞会丢失诊断线索，
        // 故无条件记一次 WARN（含元素 id + 事件标签定位）；仅在 dev-debug verbose 时附带完整堆栈避免刷屏。
        val id = target?.id() ?: "(null)"
        if (uiVerbose()) {
            LOGGER.warn("[ui] 元素 [$id] 的 $tag 处理器抛出异常，已吞掉以保持 dispatcher 一致性", t)
        } else {
            LOGGER.warn("[ui] 元素 [$id] 的 $tag 处理器抛出异常，已吞掉以保持 dispatcher 一致性（开启 dev-debug 可看堆栈）")
        }
        false
    }
}
